import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export interface FieldLearningRecord {
  id: string;
  fieldId: string;
  fieldTemplateId?: string;
  detectedName?: string;
  labelContext?: string;
  sectionContext?: string;
  precedingText?: string;
  followingText?: string;
  documentType?: string;
  source: "AUTO_ASSIGNED" | "USER_CORRECTED" | "USER_CONFIRMED";
  confidence?: number;
  predictedConfidence?: number;
  isLatest: boolean;
  companyId?: string;
  documentId?: string;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  active: boolean;
  fieldTemplate?: {
    id: string;
    name: string;
    category: string;
  };
}

export interface CreateFieldLearningRequest {
  fieldId: string;
  fieldTemplateId?: string;
  detectedName?: string;
  labelContext?: string;
  sectionContext?: string;
  precedingText?: string;
  followingText?: string;
  documentType?: string;
  source: "AUTO_ASSIGNED" | "USER_CORRECTED" | "USER_CONFIRMED";
  confidence?: number;
  predictedConfidence?: number;
  candidateRank?: number;
  companyId?: string;
  documentId?: string;
}

export interface GetRelevantExamplesParams {
  companyId: string;
  documentType?: string;
  tokenBudget?: number;
}

export const fieldLearningApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "fieldLearningApi",
    baseUrl: "/api/field-learning",
  }),
  reducerPath: "fieldLearningApi",
  tagTypes: ["FieldLearning"],
  endpoints: (builder) => ({
    getRelevantExamples: builder.query<
      { data: FieldLearningRecord[] },
      GetRelevantExamplesParams
    >({
      query: ({ companyId, documentType, tokenBudget }) => {
        const params = new URLSearchParams({ companyId });
        if (documentType) params.set("documentType", documentType);
        if (tokenBudget) params.set("tokenBudget", String(tokenBudget));
        return `/examples?${params.toString()}`;
      },
      providesTags: [{ type: "FieldLearning" }],
    }),
    createFieldLearning: builder.mutation<
      { data: FieldLearningRecord },
      CreateFieldLearningRequest
    >({
      query: (data) => ({
        url: "/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FieldLearning" }],
    }),
    getFieldLearningByFieldId: builder.query<
      { data: FieldLearningRecord[] },
      string
    >({
      query: (fieldId) => `/field/${fieldId}`,
      providesTags: [{ type: "FieldLearning" }],
    }),
    getFieldLearningStats: builder.query<
      {
        data: {
          overallAccuracy: number;
          totalAutoAssigned: number;
          totalCorrected: number;
          monthlyTrend: Array<{
            month: string;
            accuracy: number;
            total: number;
            corrected: number;
          }>;
          topCorrectedTemplates: Array<{
            templateId: string;
            templateName: string;
            category: string;
            correctionCount: number;
            totalCount: number;
            correctionRate: number;
          }>;
        };
      },
      { companyId: string }
    >({
      query: ({ companyId }) => `/stats?companyId=${companyId}`,
      providesTags: [{ type: "FieldLearning" }],
    }),
    resetFieldLearning: builder.mutation<
      { data: { count: number } },
      { companyId: string; documentType?: string }
    >({
      query: (data) => ({
        url: "/reset",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FieldLearning" }],
    }),
  }),
});

export const {
  useGetRelevantExamplesQuery,
  useCreateFieldLearningMutation,
  useGetFieldLearningByFieldIdQuery,
  useGetFieldLearningStatsQuery,
  useResetFieldLearningMutation,
} = fieldLearningApi;
